import { faSparkles } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import mixpanel from "mixpanel-browser";
import React from "react";
import { useFormContext } from "react-hook-form";

import Chip from "../../../../common/chips/Chip";
import EmphasisBanner from "../../../../common/EmphasisBanner";
import {
  FREEFORM_TEMPLATE_EXAMPLES,
  SOAP_TEMPLATE_EXAMPLES,
  TEMPLATE_FIELDS,
  TEMPLATE_OPTIONS_MAP,
  TEMPLATE_TYPES,
} from "../../../constants";
import {
  cleanExtractedData,
  processCustomFields,
  processSoapFields,
} from "../../utils";

export const TranscriptSampleChipMenu = ({
  control,
  handleClearSampleTranscript,
}) => {
  const { setValue } = useFormContext(control);
  const { watch } = useFormContext(control);
  const formData = watch();

  const mapTranscriptChips =
    formData.templateType === TEMPLATE_TYPES.CUSTOM
      ? Object.values(FREEFORM_TEMPLATE_EXAMPLES).map((sample) => ({
          name: sample.name,
          sampleTranscript: sample.sampleTranscript,
        }))
      : Object.values(SOAP_TEMPLATE_EXAMPLES).map((sample) => ({
          name: sample.name,
          sampleTranscript: sample.sampleTranscript,
        }));

  return (
    <div className="flex flex-col gap-2 bg-indigo-50 dark:bg-indigo-700/20 rounded-xl p-3">
      <p className="text-sm">
        Select a sample then click <strong>Preview Changes</strong>.
        Your template will be applied to the selected sample
        appointment.
      </p>
      <div className="flex flex-wrap gap-2">
        {mapTranscriptChips.map((sample) => (
          <Chip
            key={sample.name}
            label={sample.name}
            onClick={() => {
              setValue("sampleTranscript", sample.sampleTranscript, {
                shouldDirty: true,
              });
            }}
            isClearable={
              formData.sampleTranscript === sample.sampleTranscript
            }
            onClear={handleClearSampleTranscript}
            dataTestId={`transcript-sample-${sample.name}`}
          />
        ))}
      </div>
    </div>
  );
};

const TemplateExamplesChipMenu = ({
  control,
  handleClearInspirationChips,
}) => {
  const { setValue } = useFormContext(control);
  const { watch } = useFormContext(control);
  const formData = watch();

  function handleGeneralVariant(variant, examples, processFn) {
    const cleanedContent = cleanExtractedData(examples[variant]);
    setValue("templateFields", processFn(cleanedContent), {
      shouldDirty: true,
    });
    setValue("userPreferences", examples[variant].preferences);
    setValue("sampleTranscript", examples[variant].sampleTranscript);
    setValue("templateVariant", variant);
  }

  function handleVariantSelect(variant) {
    if (formData.templateType === TEMPLATE_TYPES.CUSTOM) {
      handleGeneralVariant(
        variant,
        FREEFORM_TEMPLATE_EXAMPLES,
        (cleaned) =>
          processCustomFields(cleaned[TEMPLATE_FIELDS.CUSTOM]),
      );
    } else if (formData.templateType === TEMPLATE_TYPES.SOAP) {
      handleGeneralVariant(
        variant,
        SOAP_TEMPLATE_EXAMPLES,
        (cleaned) => processSoapFields(cleaned),
      );
    } else {
      mixpanel.track("Invalid template type", {
        templateType: formData.templateType,
      });
    }
  }

  const options = TEMPLATE_OPTIONS_MAP[formData.templateType] || [];

  return (
    <EmphasisBanner>
      <h4 className="text-center text-sm text-gray-700 dark:text-white">
        <FontAwesomeIcon
          icon={faSparkles}
          className="text-yellow-400 mr-1"
          size="lg"
        />{" "}
        Need some inspiration? Try these samples. These will replace
        the current template.
      </h4>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          return (
            <Chip
              key={option}
              label={option}
              isClearable={formData.templateVariant == option}
              onClick={() => handleVariantSelect(option)}
              onClear={handleClearInspirationChips}
              dataTestId={`template-variant-${option}`}
            />
          );
        })}
      </div>
    </EmphasisBanner>
  );
};

export default TemplateExamplesChipMenu;
